import type { ProjectListItem, ProjectListView } from '@fos/shared/contracts/projects';
import { PROJECT_LIST_TAB_LABELS } from '@fos/shared/labels';
import { FolderKanban, Plus, SearchX } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useActiveSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { formatDateShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { ClientChip } from '../../../ui/EntityLabel';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { MobileFab, SEGMENTED_TOUCH, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { FilterMenu, type FilterOption } from '../components/FilterMenu';
import { ClientDot, ProjectHours, ProjectStatusLabel } from '../components/ProjectBits';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { isProjectOverdue, projectSubtitle } from '../project-format';
import { ProjectsServicesTab } from '../slots';

type Tab = keyof typeof PROJECT_LIST_TAB_LABELS;

/** Zakładki w adresie (`?widok=`) bez polskich znaków. */
const TAB_PARAM: Record<Tab, string> = {
  active: 'aktywne',
  services: 'uslugi',
  internal: 'wewnetrzne',
  closed: 'zakonczone',
};
const TAB_VIEW: Record<Exclude<Tab, 'services'>, ProjectListView> = {
  active: 'active',
  internal: 'internal',
  closed: 'closed',
};
const INTERNAL_FILTER = '__internal__';

function tabFromParam(value: string | null): Tab {
  const found = (Object.keys(TAB_PARAM) as Tab[]).find((t) => TAB_PARAM[t] === value);
  return found ?? 'active';
}

const EMPTY_TEXT: Record<Exclude<Tab, 'services'>, { title: string; description: string }> = {
  active: { title: 'Brak aktywnych projektów', description: 'Dodaj projekt klienta albo projekt wewnętrzny.' },
  internal: { title: 'Brak projektów wewnętrznych', description: 'Projekt wewnętrzny nie ma klienta, np. Fravity OS.' },
  closed: { title: 'Brak zakończonych projektów', description: 'Tu trafią projekty zakończone i z archiwum.' },
};

/**
 * Lista projektów (Figma 07 Projekty, 3031:3424): zakładki Aktywne / Usługi / Wewnętrzne / Zakończone,
 * filtry „Klient” i „Opiekun” (prowadzący), kolumny Projekt, Godziny, Termin, Status i prowadzący.
 * Filtry działają na wczytanej liście (projektów jest mało); serwer zwraca tylko rekordy z zakresu osoby.
 */
export function ProjectsListPage() {
  const access = useAccess();
  if (!access.can('projects')) return <ModulePage module="projects" />;
  return <ProjectsList />;
}

function ProjectsList() {
  const access = useAccess();
  const session = useActiveSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get('widok'));
  const clientFilter = params.get('klient') ?? '';
  const leadFilter = params.get('opiekun') ?? '';
  const [creating, setCreating] = useState(false);
  const phone = useIsPhone();
  const canEdit = access.can('projects', 'edit');

  const setParam = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const query = trpc.projects.list.useQuery(
    { view: tab === 'services' ? 'active' : TAB_VIEW[tab] },
    {
      enabled: tab !== 'services',
    },
  );

  const all = query.data ?? [];
  const clientOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    for (const p of all) {
      if (p.client) {
        map.set(p.client.id, { value: p.client.id, label: p.client.name, leading: <ClientDot client={p.client} /> });
      }
    }
    const list = [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
    if (all.some((p) => !p.client)) {
      list.push({ value: INTERNAL_FILTER, label: 'Bez klienta', leading: <ClientDot client={null} internal /> });
    }
    return list;
  }, [all]);
  const leadOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    for (const p of all) if (p.lead) map.set(p.lead.userId, { value: p.lead.userId, label: p.lead.name });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  }, [all]);

  const items = all.filter(
    (p) =>
      (!clientFilter || (clientFilter === INTERNAL_FILTER ? !p.client : p.client?.id === clientFilter)) &&
      (!leadFilter || p.lead?.userId === leadFilter),
  );
  const filtered = Boolean(clientFilter || leadFilter);

  const tabOptions = (Object.keys(PROJECT_LIST_TAB_LABELS) as Tab[]).map((t) => ({
    value: t,
    label: PROJECT_LIST_TAB_LABELS[t],
  }));

  let content: ReactNode;
  if (tab === 'services') content = <ProjectsServicesTab />;
  else if (query.isPending) content = <ListSkeleton rows={5} />;
  else if (query.isError) {
    content = <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />;
  } else if (items.length === 0) {
    content = filtered ? (
      <EmptyState
        icon={SearchX}
        title="Brak projektów dla tych filtrów"
        action={
          <Button
            variant="secondary"
            onClick={() => {
              setParam('klient', '');
              setParam('opiekun', '');
            }}
          >
            Wyczyść filtry
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={FolderKanban}
        title={EMPTY_TEXT[tab].title}
        description={EMPTY_TEXT[tab].description}
        action={
          canEdit && tab !== 'closed' ? (
            <Button variant="secondary" icon={Plus} onClick={() => setCreating(true)}>
              Nowy projekt
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    content = (
      <ProjectsTable
        items={items}
        workspaceName={session.workspace?.name}
        currentUserId={session.user.id}
        onOpen={(id) => navigate(`/projekty/${id}`)}
      />
    );
  }

  return (
    <Page>
      <PageHeader
        title="Projekty"
        aside={tab !== 'services' && query.data ? <CountPill count={items.length} /> : undefined}
        actions={
          canEdit ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)} className="max-md:hidden">
              Nowy projekt
            </Button>
          ) : undefined
        }
      />
      <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:gap-2.5">
        <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
          <Segmented
            aria-label="Widok listy projektów"
            className={SEGMENTED_TOUCH}
            value={tab}
            onValueChange={(t) => setParam('widok', t === 'active' ? '' : TAB_PARAM[t])}
            options={tabOptions}
          />
        </div>
        <span className="hidden flex-1 md:block" />
        {tab !== 'services' && (
          <div className="flex flex-wrap items-center gap-2.5">
            <FilterMenu
              label="Klient"
              value={clientFilter}
              onValueChange={(v) => setParam('klient', v)}
              options={clientOptions}
            />
            <FilterMenu
              label="Opiekun"
              value={leadFilter}
              onValueChange={(v) => setParam('opiekun', v)}
              options={leadOptions}
            />
          </div>
        )}
      </div>
      <Section className="mt-5">{content}</Section>
      {canEdit && phone && (
        <MobileFab icon={Plus} onClick={() => setCreating(true)}>
          Nowy projekt
        </MobileFab>
      )}
      <ProjectFormDialog open={creating} onOpenChange={setCreating} onSaved={(p) => navigate(`/projekty/${p.id}`)} />
    </Page>
  );
}

/**
 * Tabela projektów: wiersz 60 px, cały wiersz prowadzi do szczegółów (nazwa jest linkiem dla klawiatury).
 * Kolumny według szerokości, żeby nazwa projektu zawsze miała miejsce: „Godziny” od 1024 px, „Termin”
 * i „Prowadzi” od 1280 px (wcześniej termin jest w podtytule).
 */
function ProjectsTable({
  items,
  workspaceName,
  currentUserId,
  onOpen,
}: {
  items: ProjectListItem[];
  workspaceName: string | undefined;
  currentUserId: string;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="relative w-full">
      <table className="w-full table-fixed border-collapse text-left text-body-s">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="h-9 px-1 text-caption font-medium text-fg-muted">
              Projekt
            </th>
            <th scope="col" className="hidden h-9 w-[236px] px-1 text-caption font-medium text-fg-muted lg:table-cell">
              Godziny
            </th>
            <th scope="col" className="hidden h-9 w-[106px] px-1 text-caption font-medium text-fg-muted xl:table-cell">
              Termin
            </th>
            <th scope="col" className="h-9 w-[124px] px-1 text-caption font-medium text-fg-muted md:w-[146px]">
              Status
            </th>
            <th scope="col" className="hidden h-9 w-9 px-1 xl:table-cell">
              <span className="sr-only">Prowadzi</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const overdue = isProjectOverdue(p);
            return (
              <tr
                key={p.id}
                onClick={() => onOpen(p.id)}
                className="cursor-pointer border-b border-line transition-colors hover:bg-surface"
              >
                <td className="h-[60px] px-1 pr-4">
                  {/* lifting 17.10.2026: klient jako chip w swojej barwie pod nazwą (zamiast kropki i „klient · projekt”) */}
                  <div className="flex min-w-0 flex-col gap-1">
                    <Link
                      to={`/projekty/${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      title={p.name}
                      className="truncate rounded-xs font-semibold text-fg focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      {p.name}
                    </Link>
                    <span className="flex min-w-0 items-center gap-1.5 text-caption text-fg-muted">
                      {p.client && !p.isInternal ? (
                        <ClientChip client={p.client} />
                      ) : (
                        <span className="truncate">{projectSubtitle(p, workspaceName)}</span>
                      )}
                      {p.endDate && (
                        <span className={cn('shrink-0 xl:hidden', overdue && 'font-medium text-danger')}>
                          do {formatDateShort(p.endDate)}
                        </span>
                      )}
                    </span>
                  </div>
                </td>
                <td className="hidden px-1 pr-4 lg:table-cell">
                  <ProjectHours usedSeconds={p.usedSeconds} budgetSeconds={p.hoursBudgetSeconds} />
                </td>
                <td
                  className={cn(
                    'hidden px-1 pr-4 whitespace-nowrap xl:table-cell',
                    overdue ? 'text-danger' : 'text-fg-secondary',
                  )}
                >
                  {p.endDate ? formatDateShort(p.endDate) : '—'}
                </td>
                <td className="px-1">
                  <ProjectStatusLabel project={p} />
                </td>
                <td className="hidden px-1 xl:table-cell">
                  {p.lead && (
                    <Avatar
                      initials={p.lead.initials}
                      color={p.lead.userId === currentUserId ? null : p.lead.avatarColor}
                      title={`Prowadzi: ${p.lead.name}`}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
